/**
 * Protected Route - Authentication guard for routes
 *
 * Redirects to login if user is not authenticated.
 * Use as a wrapper route element in React Router.
 *
 * @example
 * ```tsx
 * <Route element={<ProtectedRoute />}>
 *   <Route path="/dashboard" element={<Dashboard />} />
 *   <Route path="/settings" element={<Settings />} />
 * </Route>
 * ```
 */

import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { useSession } from '@entities/session';

interface ProtectedRouteProps {
  /** Optional: Custom redirect path (default: /login) */
  redirectTo?: string;
}

export function ProtectedRoute({ redirectTo = '/login' }: ProtectedRouteProps) {
  const { user, loading } = useSession();
  const location = useLocation();

  // While loading, the AuthProvider shows the spinner
  // This is just a safety check
  if (loading) {
    return null;
  }

  // Not authenticated - redirect to login, keeping the query string.
  // E-mail sign-in links continue to "/" (see
  // functions/src/callable/request-sign-in-link.ts) and carry mode/oobCode
  // there while nobody is signed in yet — dropping them here would make every
  // sign-in link dead on arrival. Same reasoning as EducatorLoginRedirect in
  // routes.tsx.
  if (!user) {
    return <Navigate to={`${redirectTo}${location.search}${location.hash}`} replace />;
  }

  // Authenticated - render child routes
  return <Outlet />;
}
